/**
 * What the public verify pages and the My Account users table say about an
 * account_user status.
 *
 * keyon answers Expired for a pending account whose invitation has run out -
 * 30 days after the last invite, or after the account was created when none was
 * ever sent - whether or not its nightly sweep has stored it yet
 * (keyon/lib/PendingAccountExpiry.php). Sending the invitation again revives
 * it, so My Account opens the same Resend Invitation modal for Expired as for
 * Pending.
 */

/** keyon's PendingAccountExpiry::MESSAGE, word for word. */
export const INVITE_EXPIRED = 'This invitation has expired. Please ask your organization to send a new one.';
export const ALREADY_VERIFIED = 'This account has already been verified.';

const normalized = (status) => String(status == null ? '' : status).trim().toLowerCase();

export function isExpiredStatus(status) {
  return normalized(status) === 'expired';
}

/** Not yet verified: Pending, or Expired (which a resend revives). */
export function isUnverifiedStatus(status) {
  const s = normalized(status);
  return s === 'pending' || s === 'expired';
}

/** The label of a not-yet-verified status, for a button that opens the resend. */
export function unverifiedLabel(status) {
  return isExpiredStatus(status) ? 'Expired' : 'Pending';
}

/** The sentence a verify page shows when the account is not Pending. */
export function notPendingMessage(status) {
  return isExpiredStatus(status) ? INVITE_EXPIRED : ALREADY_VERIFIED;
}

const escapeHtml = (text) =>
  String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * DataTables render for My Account's account_status column. Pending and
 * Expired are links the page opens the Resend Invitation modal from
 * (.account-status-pending-link); anything else is text. Sort, filter and type
 * get the plain label.
 */
export function accountStatusCell(data, type) {
  const raw = String(data == null ? '' : data).trim();
  const status = raw.toLowerCase();
  const label =
    status === 'pending' ? 'Pending' : status === 'active' ? 'Active' : status === 'expired' ? 'Expired' : raw || '—';
  if (type !== 'display') return label;
  if (status === 'pending' || status === 'expired') {
    return '<a href="#" class="account-status-pending-link">' + label + '</a>';
  }
  return escapeHtml(label);
}

/**
 * Ask keyon again, just before a verify page finalizes, whether the account is
 * still Pending. The page read it when it opened; the invitation can run out
 * while the form is being filled in, and finalizing in pacmny-be (a working
 * login) before keyon refuses to activate left a person signed in to an
 * account keyon still calls pending and later expires.
 *
 * @param {(request: object) => Promise<{data: object}>} webHook the page's keyon call
 * @returns {Promise<string|null>} null to go ahead, else the sentence to show
 */
export async function recheckPendingAccount(webHook, accountType, uniqueNine) {
  let res;
  try {
    res = await webHook({ Account: { API_Meth: 'GetPendingAccountInfo', accountype: accountType, uniqueNine } });
  } catch (err) {
    return 'We could not check your invitation just now. Please try again in a few minutes.';
  }
  const row = res && res.data && Array.isArray(res.data.data) ? res.data.data[0] : null;
  if (!row) {
    return 'There is an error with this verification link. Please request a new one.';
  }
  return normalized(row.account_status) === 'pending' ? null : notPendingMessage(row.account_status);
}
